/**
 * The forecast for one spot on the chart, hour by hour, model by model.
 *
 * Long-press the water and this is what "Wind here" opens. It used to pin a
 * single synthetic barb; a table of real models is strictly more use and this is
 * where Edd asked for it. Pinning a barb is still here, as a button, because
 * having the arrow on the chart is a different job.
 *
 * The shape of it is deliberate. Models across, hours down, one row per hour with
 * the models' own figures side by side and the agreed figure at the end. Reading
 * across a row is the whole point: three columns saying 18, 19, 21 is a forecast
 * you can plan on, and three saying 12, 19, 34 is a day to stay in, and no single
 * averaged number can tell those apart.
 *
 * Nothing here invents a figure. An hour a model has no answer for is blank, and
 * a model that failed says why at the top of its column rather than quietly
 * leaving one fewer opinion on screen than the skipper thinks they are reading.
 */
import { useEffect, useMemo, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useEnvStore } from '@/store/environment';
import { useForecastTableStore } from '@/store/forecastTable';
import { WEATHER_MODELS, modelById, resolutionLabel } from '@/weather/models';
import { WEATHER_VARIABLES, valuesAt, daysIn, variableById } from '@/weather/multiModel';
import {
  agreeScalar,
  agreeDirection,
  agreementNote,
  AGREEMENT_THRESHOLDS as TH,
  type Consensus,
} from '@/weather/consensus';
import { formatBearing, formatLatLon } from '@/nav';
import { cellPaint, arrowRotationDeg } from '@/weather/tableColours';
import { windAt } from '@/weather';
import { Sheet } from './Sheet';

/**
 * The variables offered as table columns, in the order a sailor reads them.
 * Gust and direction no longer get pages of their own: the wind cell carries
 * both (speed in the roundel, "+N" for the gust over it, the arrow for the
 * direction), which is how the eye actually reads a forecast — one cell, one
 * hour, the whole wind story (fb-msn90tb7-0). Cloud takes the freed slot
 * (fb-msn92714-2).
 */
const SHOWN = ['wind', 'rain', 'cloud', 'pressure'] as const;

const thresholdFor = (id: string) =>
  id === 'dir'
    ? TH.dirDeg
    : id === 'gust'
      ? TH.gustKn
      : id === 'rain'
        ? TH.rainMm
        : id === 'pressure'
          ? TH.pressureHpa
          : TH.windKn;

function fmt(value: number | null, dp: number, isDir: boolean): string {
  if (value == null) return '—';
  return isDir ? formatBearing(value) : value.toFixed(dp);
}

/** Tight / fair / poor, as a class the stylesheet can colour. */
function agreementClass(c: Consensus): string {
  if (!c.count) return '';
  return `acp-agree-${c.agreement}`;
}

/**
 * The chart overlay's arrow, table-sized: a shaft and a two-stroke head in the
 * cell's own ink, pointing the way the wind is going (fb-msmawtqn-1, "can we
 * have the arrows like we have on the chart overlays?"). An SVG rather than a
 * glyph, because the ↑ character it replaces rendered at whatever weight the
 * system font fancied and never matched the chart.
 */
function WindArrow({ dirFromDeg }: { dirFromDeg: number }) {
  return (
    <svg
      viewBox="0 0 12 16"
      width={10}
      height={13}
      style={{ transform: `rotate(${arrowRotationDeg(dirFromDeg)}deg)`, verticalAlign: '-2px', marginRight: 3 }}
      aria-hidden="true"
    >
      <g fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 14 L6 2" />
        <path d="M2.5 6 L6 2 L9.5 6" />
      </g>
    </svg>
  );
}

function dayLabel(ms: number): string {
  const d = new Date(ms);
  const wd = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getUTCDay()];
  return `${wd} ${d.getUTCDate()}`;
}

export function WeatherTableSheet() {
  const chartTap = useShellStore((s) => s.chartTap);
  const weatherSource = useEnvStore((s) => s.weatherSource);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const setPinnedWind = useEnvStore((s) => s.setPinnedWind);

  const modelIds = useForecastTableStore((s) => s.modelIds);
  const toggleModel = useForecastTableStore((s) => s.toggleModel);
  const table = useForecastTableStore((s) => s.table);
  const status = useForecastTableStore((s) => s.status);
  const error = useForecastTableStore((s) => s.error);
  const load = useForecastTableStore((s) => s.load);

  const [variableId, setVariableId] = useState<string>('wind');
  const [dayIndex, setDayIndex] = useState(0);
  /**
   * Row spacing: every hour, or every third. Three-hourly is how XC Weather
   * reads a whole day in one screen, and hourly is what you want deciding when
   * to actually leave. Both are the same fetched table, just fewer rows.
   */
  const [stepH, setStepH] = useState<1 | 3>(3);
  const [showModels, setShowModels] = useState(false);
  /** Wind direction in the cells: the overlay's arrow, or figures in degrees
   *  for anyone writing a passage log (fb-msn91wyv-1). */
  const [dirAs, setDirAs] = useState<'arrow' | 'degrees'>('arrow');

  const lon = chartTap ? Number(chartTap[0].toFixed(2)) : null;
  const lat = chartTap ? Number(chartTap[1].toFixed(2)) : null;
  useEffect(() => {
    if (lon == null || lat == null) return;
    void load([lon, lat]);
  }, [load, lon, lat, modelIds]);

  const days = useMemo(() => (table ? daysIn(table) : []), [table]);
  const day = days[Math.min(dayIndex, Math.max(0, days.length - 1))] ?? null;
  const hours = useMemo(
    () =>
      table && day != null
        ? table.timesMs.filter(
            (t) => t >= day && t < day + 86_400_000 && new Date(t).getUTCHours() % stepH === 0,
          )
        : [],
    [table, day, stepH],
  );

  const variable = variableById(variableId) ?? WEATHER_VARIABLES[0];

  if (!chartTap) {
    return (
      <Sheet name="weathertable" title="Forecast here" sub="Long-press a spot on the chart to read it.">
        <p className="acp-note">No spot chosen yet.</p>
      </Sheet>
    );
  }

  return (
    <Sheet
      name="weathertable"
      title="Forecast here"
      sub={`${formatLatLon(chartTap[1], 'lat')} ${formatLatLon(chartTap[0], 'lon')} — several models side by side, so you can see where they disagree.`}
    >
      <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
        {SHOWN.map((id) => {
          const v = variableById(id);
          if (!v) return null;
          return (
            <button
              key={id}
              type="button"
              className={variableId === id ? 'on' : ''}
              onClick={() => setVariableId(id)}
            >
              {id === 'wind' ? 'Wind' : v.name} <small style={{ opacity: 0.75 }}>{v.unit}</small>
            </button>
          );
        })}
        <button
          type="button"
          className="acp-fc-step"
          onClick={() => setDirAs((d) => (d === 'arrow' ? 'degrees' : 'arrow'))}
          title="Show wind direction as the arrow or in degrees"
        >
          {dirAs === 'arrow' ? 'Dir: arrow' : 'Dir: degrees'}
        </button>
        <button
          type="button"
          className="acp-fc-step"
          onClick={() => setStepH((h) => (h === 1 ? 3 : 1))}
          title="Row spacing: every hour, or every third"
        >
          {stepH === 1 ? '1 h' : '3 h'}
        </button>
      </div>

      {status === 'loading' && <p className="acp-note">Fetching {modelIds.length} models…</p>}
      {status === 'error' && <p className="acp-note">Couldn’t fetch: {error ?? 'network error'}.</p>}

      {table && days.length > 0 && (
        <>
          <div className="acp-seg" style={{ flexWrap: 'wrap', marginTop: 8 }}>
            {days.map((d, i) => (
              <button key={d} type="button" className={i === dayIndex ? 'on' : ''} onClick={() => setDayIndex(i)}>
                {dayLabel(d)}
              </button>
            ))}
          </div>

          <table className="acp-fctable">
            <thead>
              <tr>
                {/* The unit sits in the corner cell so every figure below has
                    its unit one glance up (fb-msmrbiry-0: "is this in knots?"). */}
                <th>
                  UT <small style={{ opacity: 0.75 }}>· {variable.unit}</small>
                </th>
                {table.series.map((s) => {
                  const m = modelById(s.modelId);
                  return (
                    <th key={s.modelId} title={m ? `${m.provider} · ${resolutionLabel(m)}` : s.modelId}>
                      {m?.name ?? s.modelId}
                      {s.error && <span className="acp-fc-dead"> ✕</span>}
                    </th>
                  );
                })}
                <th>Agreed</th>
              </tr>
            </thead>
            <tbody>
              {hours.map((ms) => {
                const vals = valuesAt(table, variableId, ms);
                const c = variable.isDirection
                  ? agreeDirection(vals, thresholdFor(variableId))
                  : agreeScalar(vals, thresholdFor(variableId));
                const hh = String(new Date(ms).getUTCHours()).padStart(2, '0');
                // The wind view is the XC Weather cell: an arrow the way it is
                // blowing, and "mean–gust" as one reading, because the gap
                // between those two numbers is the briefing. 14 gusting 16 is a
                // steady sail; 14 gusting 30 is a reef before you need one.
                const isWind = variableId === 'wind';
                const gusts = isWind ? valuesAt(table, 'gust', ms) : [];
                const dirs = isWind ? valuesAt(table, 'dir', ms) : [];
                const gustBy = new Map(gusts.map((g) => [g.modelId, g.value] as const));
                const dirBy = new Map(dirs.map((d) => [d.modelId, d.value] as const));
                const gAgree = isWind ? agreeScalar(gusts, thresholdFor('gust')) : null;
                const dAgree = isWind ? agreeDirection(dirs, thresholdFor('dir')) : null;
                const cell = (value: number | null, gust: number | null, dir: number | null) => {
                  const p = cellPaint(variableId, value);
                  const style = p ? { background: p.bg, color: p.fg } : undefined;
                  if (!isWind) {
                    return { style, body: fmt(value, variable.dp, !!variable.isDirection) };
                  }
                  if (value == null) return { style, body: '—' };
                  // The overlay's grammar in a cell (fb-msn90tb7-0): the speed
                  // in a roundel, the arrow the way it is blowing, and the gust
                  // as "+N" over the mean, because 14 gusting 30 is a reef and
                  // 14 gusting 16 is a sail, and the DIFFERENCE is the story.
                  const gustOver = gust != null ? Math.max(0, Math.round(gust - value)) : null;
                  return {
                    style,
                    body: (
                      <>
                        {dir != null &&
                          (dirAs === 'arrow' ? (
                            <WindArrow dirFromDeg={dir} />
                          ) : (
                            <small style={{ marginRight: 3, opacity: 0.85 }}>{formatBearing(dir)}</small>
                          ))}
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 20,
                            padding: '0 3px',
                            borderRadius: 999,
                            border: '1.4px solid currentColor',
                            textAlign: 'center',
                          }}
                        >
                          {value.toFixed(0)}
                        </span>
                        {gustOver != null && gustOver > 0 && (
                          <small style={{ marginLeft: 2, opacity: 0.9 }}>+{gustOver}</small>
                        )}
                      </>
                    ),
                  };
                };
                // The hour we are IN, not the nearest: the row holds until the
                // clock actually leaves it (fb-msmxz2d7-0).
                const nowRow = Date.now() >= ms && Date.now() < ms + stepH * 3_600_000;
                return (
                  <tr key={ms} style={nowRow ? { outline: '2px solid #cc1f8f', outlineOffset: -2 } : undefined}>
                    <td className="acp-fc-hour">{nowRow ? <b>{hh}</b> : hh}</td>
                    {vals.map((v) => {
                      const k = cell(v.value, gustBy.get(v.modelId) ?? null, dirBy.get(v.modelId) ?? null);
                      return (
                        <td
                          key={v.modelId}
                          style={k.style}
                          className={c.outliers.includes(v.modelId) ? 'acp-fc-outlier' : undefined}
                        >
                          {k.body}
                        </td>
                      );
                    })}
                    {(() => {
                      const k = cell(c.value, gAgree?.value ?? null, dAgree?.value ?? null);
                      return (
                        <td style={k.style} className={agreementClass(c)}>
                          {k.body}
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className="acp-note">
            {(() => {
              const noon = hours[Math.floor(hours.length / 2)];
              if (noon == null) return null;
              const vals = valuesAt(table, variableId, noon);
              const c = variable.isDirection
                ? agreeDirection(vals, thresholdFor(variableId))
                : agreeScalar(vals, thresholdFor(variableId));
              return agreementNote(c);
            })()}{' '}
            The last column is the middle of the models, not a model of its own. A figure marked out
            in a column is one sitting well away from the others: it is shown, not dropped, because
            nothing here can know which model is the wrong one.
          </p>
        </>
      )}

      <button
        type="button"
        className="acp-btn full"
        style={{ marginTop: 8 }}
        onClick={() => setShowModels((v) => !v)}
      >
        {showModels ? 'Hide models' : `Models (${modelIds.length} of ${WEATHER_MODELS.length})`}
      </button>

      {showModels && (
        <div style={{ marginTop: 8 }}>
          {WEATHER_MODELS.map((m) => (
            <label key={m.id} className="acp-check">
              <input type="checkbox" checked={modelIds.includes(m.id)} onChange={() => toggleModel(m.id)} />
              <span>
                <strong>{m.name}</strong> · {m.provider} · {resolutionLabel(m)} · {m.days} days
                <br />
                <span className="acp-menu-note">{m.note}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="acp-btn-row">
        <button
          type="button"
          className="acp-btn full"
          onClick={() => {
            const w = windAt(weatherSource, streamTimeMs, chartTap);
            setPinnedWind({ position: chartTap, dirFromDeg: w.dirFromDeg, speedKn: w.speedKn });
          }}
        >
          Pin a barb here
        </button>
      </div>
    </Sheet>
  );
}
